// RGB canvas → I420 BT.709 limited, packed on the GPU so readPixels returns the exact plane bytes (F-13.4).
// Encoding our own YUV avoids the browser's RGB→YUV conversion, whose matrix is not specified (S-07).

const VS = `#version 300 es
void main() {
  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`;

// Each output texel packs 4 consecutive samples of one plane row into RGBA.
const FS = `#version 300 es
precision highp float;
uniform sampler2D uSrc;     // RGBA8, row 0 = top of the image (uploaded with FLIP_Y false)
uniform ivec2 uSize;        // source size
uniform int uPlane;         // 0 = Y, 1 = Cb, 2 = Cr
out vec4 outColor;

vec3 rgbAt(ivec2 p) {
  p = clamp(p, ivec2(0), uSize - 1);
  return texelFetch(uSrc, p, 0).rgb;
}

float sampleOf(ivec2 pos) {
  // BT.709 limited: Y = 16 + 219·Y', Cb/Cr = 128 + 224·(B'−Y')/1.8556, (R'−Y')/1.5748
  if (uPlane == 0) {
    vec3 c = rgbAt(pos);
    float y = dot(c, vec3(0.2126, 0.7152, 0.0722));
    return (16.0 + 219.0 * y) / 255.0;
  }
  // Chroma: average of the 2×2 block (centre-sited, matches common H.264 decoders closely enough for 8-bit output).
  ivec2 b = pos * 2;
  vec3 c = (rgbAt(b) + rgbAt(b + ivec2(1, 0)) + rgbAt(b + ivec2(0, 1)) + rgbAt(b + ivec2(1, 1))) * 0.25;
  float y = dot(c, vec3(0.2126, 0.7152, 0.0722));
  float v = uPlane == 1 ? (c.b - y) / 1.8556 : (c.r - y) / 1.5748;
  return (128.0 + 224.0 * v) / 255.0;
}

void main() {
  ivec2 o = ivec2(gl_FragCoord.xy);
  int x = o.x * 4;
  outColor = vec4(sampleOf(ivec2(x, o.y)), sampleOf(ivec2(x + 1, o.y)), sampleOf(ivec2(x + 2, o.y)), sampleOf(ivec2(x + 3, o.y)));
}`;

export class YuvPacker {
  private gl: WebGL2RenderingContext;
  private prog: WebGLProgram;
  private src: WebGLTexture;
  private targets: { tex: WebGLTexture; fb: WebGLFramebuffer; w: number; h: number }[] = [];
  private uSrc: WebGLUniformLocation | null;
  private uSize: WebGLUniformLocation | null;
  private uPlane: WebGLUniformLocation | null;
  private vao: WebGLVertexArrayObject;

  constructor(
    readonly width: number,
    readonly height: number,
  ) {
    if (width % 8 || height % 2) throw new Error('Export size must be a multiple of 8×2');
    const c = new OffscreenCanvas(1, 1);
    this.gl = c.getContext('webgl2', { antialias: false, premultipliedAlpha: false })!;
    const gl = this.gl;
    const sh = (type: number, src: string) => {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(String(gl.getShaderInfoLog(s)));
      return s;
    };
    this.prog = gl.createProgram()!;
    gl.attachShader(this.prog, sh(gl.VERTEX_SHADER, VS));
    gl.attachShader(this.prog, sh(gl.FRAGMENT_SHADER, FS));
    gl.linkProgram(this.prog);
    this.uSrc = gl.getUniformLocation(this.prog, 'uSrc');
    this.uSize = gl.getUniformLocation(this.prog, 'uSize');
    this.uPlane = gl.getUniformLocation(this.prog, 'uPlane');
    this.vao = gl.createVertexArray()!;
    this.src = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, this.src);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    const dims = [
      [width / 4, height],
      [width / 8, height / 2],
      [width / 8, height / 2],
    ];
    for (const [w, h] of dims) {
      const tex = gl.createTexture()!;
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      const fb = gl.createFramebuffer()!;
      gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
      this.targets.push({ tex, fb, w, h });
    }
  }

  /** Returns I420 bytes (Y, then Cb, then Cr) for the canvas contents. */
  pack(source: OffscreenCanvas | HTMLCanvasElement | ImageBitmap): Uint8Array {
    const gl = this.gl;
    const W = this.width;
    const H = this.height;
    gl.bindTexture(gl.TEXTURE_2D, this.src);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, source);
    const out = new Uint8Array(W * H + 2 * (W / 2) * (H / 2));
    let offset = 0;
    gl.useProgram(this.prog);
    gl.bindVertexArray(this.vao);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.src);
    gl.uniform1i(this.uSrc, 0);
    gl.uniform2i(this.uSize, W, H);
    for (let plane = 0; plane < 3; plane++) {
      const t = this.targets[plane];
      gl.bindFramebuffer(gl.FRAMEBUFFER, t.fb);
      gl.viewport(0, 0, t.w, t.h);
      gl.uniform1i(this.uPlane, plane);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      // Framebuffer row r = image row r (texture uploaded top-first), so rows come out top-first.
      gl.readPixels(0, 0, t.w, t.h, gl.RGBA, gl.UNSIGNED_BYTE, out.subarray(offset, offset + t.w * t.h * 4));
      offset += t.w * t.h * 4;
    }
    return out;
  }

  dispose(): void {
    this.gl.getExtension('WEBGL_lose_context')?.loseContext();
  }
}
